import React, { useEffect, useState } from 'react';
import { Grid, Box, Divider } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useRouteMatch } from 'react-router-dom';
import PropTypes from 'prop-types';

import { TitleContainer, ContentContainer, MapWrapper, FilterYearComparison } from 'src/components';
import { useProjectApi } from 'src/services';
import { AnalyticCard, IndicatorCard } from 'src/scenes/Project/components';
import { LoadingBox } from 'src/scenes/Project/scenes/Dashboard/components';
import { SitesSummaryTable } from 'src/scenes/Project/scenes/Sites/components';
import { SpeciesTrendsChart, EcosystemInventoryTable } from 'src/scenes/Project/components';
import { useFetchStatus } from 'src/hooks';
import { buildLeafletLayer } from 'src/scenes/Project/scenes/Analytics/smaAnalyticsHelper';
import { Terrain, MapMarkerMultiple, Speedometer, TextBoxCheckOutline } from 'src/utils/mdIcons';


const mapOptions = {
  center: [ -32.48431324745671, -71.25296320202341 ],
  zoomControl: true,
  dragging: true,
  zoom: 16,
  doubleClickZoom: false,
  attributionControl: false,
};



const useStyles = makeStyles(theme => ({
  group: {
    padding: '3px',
    borderRadius: theme.shape.borderRadius,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.common.gray400,
  },
  indicator: {
    height: '100%',
  },
  comparisonYearGroup: {
    border: `1px solid ${theme.palette.common.gray}`,
    borderRadius: theme.shape.borderRadius,
  },
}));

const SitesOverviewContainer = props => {
  const classes = useStyles();
  const match = useRouteMatch();
  const { projectId } = match.params;

  const projectApi = useProjectApi();
  const [ sitesInterestArea, setSitesInterestArea ] = useState([]);
  const [ sites, setSites ] = useState([]);
  const [ indicatorsData, setIndicatorsData ] = useState(null);
  const [ speciesData, setSpeciesData ] = useState([]);
  const [ sitesData, setSitesData ] = useState([]);
  const [ ecosystems, setEcosystems ] = useState(null);
  const [ ecosystemComparisonYears, setEcosystemComparisonYears ] = useState([ 2020, 2024 ]);

  // Columnas para la tabla de ecosistemas
  const ecosystemTableColumns = [
    'ecosystem',
    'conservationStatus',
    `area${ecosystemComparisonYears[0]}`,
    `condition${ecosystemComparisonYears[0]}`,
    `area${ecosystemComparisonYears[1]}`,
    `condition${ecosystemComparisonYears[1]}`,
    'performance',
  ];

  const {
    isLoading: isLoadingSitesInterestArea,
    isSuccess: isSuccessSitesInterestArea,
    handleConfirm: fetchSitesInterestArea,
  } = useFetchStatus({
    confirmFn: async () => {
      const layers = await projectApi.getSitesInterestArea({ projectId });
      setSitesInterestArea(layers);
    },
  });

  useEffect(() => {
    fetchSitesInterestArea();
  }, [ ]); // eslint-disable-line

  const { isLoading: isLoadingSites, handleConfirm: fetchSites } = useFetchStatus({
    confirmFn: async () => {
      const { projectList } = await projectApi.getProjects({ folderId: projectId });
      setSites(projectList[0]?.sites || []);
    },
  });

  useEffect(() => {
    fetchSites();
  }, []); // eslint-disable-line

  const {
    handleConfirm: fetchEcosystems,
    isSuccess: isSuccessEcosystems,
    isLoading: isLoadingEcosystems,
  } = useFetchStatus({
    confirmFn: async () => {
      const ecosystems = await projectApi.getEcosystems({ projectId, type: 'by-sites' });
      if (ecosystems?.availableYears?.length) {
        const availableYears = ecosystems.availableYears;
        const firstYear = availableYears.find(year => year === 2021) ?? availableYears[0];
        const secondYear = availableYears[availableYears.length - 1];
        setEcosystemComparisonYears([ firstYear, secondYear ]);
      }
      setEcosystems(ecosystems);
    },
  });

  useEffect(() => {
    fetchEcosystems();
  }, []); //eslint-disable-line

  const {
    isLoading: isLoadingOtherStats,
    handleConfirm: fetchOtherStats,
  } = useFetchStatus({
    confirmFn: async () => {
      const { sitesOverviewIndicators, sitesOverviewPopulation, sitesOverviewArea } = await projectApi.getProjectOtherStats({ projectId });

      const transformedIndicators = {
        totalArea: {
          title: 'Superficie total',
          value: sitesOverviewIndicators?.totalArea?.value || 0,
          unit: 'Ha',
        },
        generalCondition: {
          title: 'Condición general',
          value: sitesOverviewIndicators?.overallCondition?.value || 0,
          variation: sitesOverviewIndicators?.overallCondition?.variation,
        },
        naturalVegetationProportion: {
          title: 'Proporción de Vegetación Natural (PVN)',
          value: sitesOverviewIndicators?.naturalVegetationProportion?.value || 0,
          variation: sitesOverviewIndicators?.naturalVegetationProportion?.variation,
          unit: '%',
        },
      };
      setIndicatorsData(transformedIndicators);
      setSpeciesData(sitesOverviewPopulation || []);
      setSitesData(sitesOverviewArea || []);
    },
  });

  useEffect(() => {
    fetchOtherStats();
  }, []); // eslint-disable-line

  const handleEcosystemYearUpdate = newYears => {
    setEcosystemComparisonYears(newYears);
  };

  const hasSpecies = speciesData.length > 0;
  const hasSitesData = sitesData.length > 0;
  const hasEcosystems = ecosystems?.ecosystems?.length > 0;

  return (
    <Grid container>
      <TitleContainer
        project={props.project}
        breadcrumbs={[ 'projects', 'project', { name: 'Vision general' } ]}>
        Sitios - Visión general
      </TitleContainer>
      <ContentContainer maxWidth="xl">
        <Grid container spacing={1}>
          <Grid container item xs={12} spacing={1}>
            <Grid item xs={6}>
              <AnalyticCard title="Localización de sitios">
                <Divider />
                <Box alignItems="center" my={0.5} />
                <Box className={classes.group}>
                  {(isLoadingSitesInterestArea || isLoadingOtherStats) && <LoadingBox label="áreas de interés" />}
                  {isSuccessSitesInterestArea &&
                    <>
                      <MapWrapper
                        id='sites-interest-area-map'
                        className={classes.map}
                        options={mapOptions}
                        height={400}
                        layers={
                          [
                            ...sitesInterestArea.map(sia => buildLeafletLayer({
                              features: sia.geojson,
                              id: sia.id,
                              name: sia.name,
                            })),
                          ]
                        }
                        fitBoundsOnUpdate
                      />
                    </>
                  }
                </Box>
              </AnalyticCard>
            </Grid>
            <Grid container item xs={6} spacing={1}>
              <Grid item xs={6}>
                <IndicatorCard
                  title={indicatorsData?.totalArea?.title || 'Superficie total'}
                  number={indicatorsData?.totalArea?.value || 0}
                  className={classes.indicator}
                  Icon={Terrain}
                  color="primary"
                  isLoading={isLoadingSites || isLoadingOtherStats}
                  unit={indicatorsData?.totalArea?.unit || 'Ha'}
                />
              </Grid>
              <Grid item xs={6}>
                <IndicatorCard
                  title="Número de sitios"
                  number={sites.length}
                  className={classes.indicator}
                  Icon={MapMarkerMultiple}
                  color="secondary"
                  isLoading={isLoadingSites}
                  unit="sitios"
                />
              </Grid>
              <Grid item xs={6}>
                <IndicatorCard
                  title={indicatorsData?.generalCondition?.title || 'Condición general'}
                  number={indicatorsData?.generalCondition?.value || 0}
                  className={classes.indicator}
                  Icon={Speedometer}
                  color="fauna"
                  isLoading={isLoadingSites || isLoadingOtherStats}
                  variation={indicatorsData?.generalCondition?.variation}
                />
              </Grid>
              <Grid item xs={6}>
                <IndicatorCard
                  title={indicatorsData?.naturalVegetationProportion?.title || 'Proporción de Vegetación Natural (PVN)'}
                  number={`${indicatorsData?.naturalVegetationProportion?.value || 0}%`}
                  className={classes.indicator}
                  Icon={TextBoxCheckOutline}
                  color="flora"
                  isLoading={isLoadingSites || isLoadingOtherStats}
                  variation={indicatorsData?.naturalVegetationProportion?.variation}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid container item xs={12} spacing={1}>
            <Grid item xs={12}>
              {(hasEcosystems || isLoadingEcosystems) &&
                <>
                  <AnalyticCard
                    title="Ecosistemas conjunto de sitios"
                    ActionComponent={() => hasEcosystems ?
                      <Box display="flex" alignItems="center">
                        <Box className={classes.comparisonYearGroup} height={34}>
                          <FilterYearComparison
                            id="ecosystem-year-comparison"
                            prevValue={ecosystemComparisonYears}
                            options={ecosystems.availableYears}
                            onChange={handleEcosystemYearUpdate}
                          />
                        </Box>
                      </Box> : null
                    }
                  >
                    <Divider />
                    <Box my={1} />
                    {isLoadingEcosystems && <LoadingBox label="Ecosistemas" />}
                    {isSuccessEcosystems && ecosystems &&
                      <EcosystemInventoryTable
                        ecosystems={ecosystems}
                        comparisonYears={ecosystemComparisonYears}
                        viewMode="vegetation-floor"
                        areaType="area"
                        visibleColumns={ecosystemTableColumns}
                      />
                    }
                  </AnalyticCard>
                  <Box my={1} />
                </>
              }
              {(hasSitesData || isLoadingOtherStats) &&
                <>
                  <AnalyticCard title="Resumen de sitios">
                    <Divider />
                    <Box my={1} />
                    <SitesSummaryTable sites={sitesData} isLoading={isLoadingOtherStats} />
                  </AnalyticCard>
                  <Box my={1} />
                </>
              }
              {(hasSpecies || isLoadingOtherStats) &&
                <AnalyticCard title="Abundancia de especies en estado de amenaza">
                  <Divider />
                  <Box my={1} />
                  <SpeciesTrendsChart speciesData={speciesData} />
                </AnalyticCard>
              }
            </Grid>
          </Grid>
        </Grid>
      </ContentContainer>
    </Grid>
  );
};

SitesOverviewContainer.propTypes = {
  project: PropTypes.shape({
    siteName: PropTypes.string,
    siteId: PropTypes.number,
    name: PropTypes.string,
    id: PropTypes.number,
  }),
};


export {
  SitesOverviewContainer,
};
